import {
  type Column_RowSorting,
  type ColumnDef_RowSorting,
  copyInstancePropertiesWithoutMemos,
  type Row,
  type RowData,
  type RowModel,
  type SortFn,
  type SortingState,
  type Table,
  type TableFeatures,
  tableMemo,
} from '@tanstack/react-table';
import { compare, type SortKey } from '../../helpers/column-sort';

/**
 * The library's `sortUndefined` column option, projected off its own type so a
 * change to the accepted values breaks the build instead of drifting silently.
 */
type SortUndefined = ColumnDef_RowSorting<TableFeatures, RowData>['sortUndefined'];

/**
 * The library types `Table` and `Column` as a union over which features are
 * registered, so `atoms`, `getCanSort` and `getSortFn` only become visible
 * once TFeatures is a concrete feature set. This model is only ever installed
 * next to `rowSortingFeature`, but naming `AppTableFeatures` here would be
 * circular — that type is built *from* this function. Spelling out the handful
 * of members we touch says the requirement precisely; the library's own
 * implementation reaches for an internal cast at the same point.
 */
type SortingTable<TFeatures extends TableFeatures, TData extends RowData> = {
  atoms: { sorting?: { get: () => SortingState | undefined } };
  getPreSortedRowModel: () => RowModel<TFeatures, TData>;
  getColumn: (columnId: string) => SortingColumn<TFeatures, TData> | undefined;
};

type SortingColumn<TFeatures extends TableFeatures, TData extends RowData> = Pick<
  Column_RowSorting<TFeatures, TData>,
  'getCanSort' | 'getSortFn'
> & {
  columnDef: Pick<ColumnDef_RowSorting<TFeatures, TData>, 'sortUndefined' | 'invertSorting'> & {
    meta?: { sortKey?: (value: TData) => SortKey };
  };
};

/**
 * A sorted row model that computes each row's sort key once, instead of once
 * per comparison.
 *
 * TanStack's own `createSortedRowModel` calls the column's `sortFn` for every
 * comparison, and a comparator can only reach a row's sort value by deriving it
 * on the spot. Sorting 13,000 rows takes ~178,000 comparisons, so a derived
 * accessor runs ~356,000 times per sort even though there are only 13,000
 * distinct answers. On the order-edit table that sort runs on every keystroke.
 *
 * This version reads `meta.sortKey` (see `defineColumn`) to decorate first:
 * one key per row per sort level, then a comparison that only indexes arrays.
 * Columns that pass their own `sortingFn` publish no key and fall through to
 * the comparator path unchanged, as do `sortUndefined` and `invertSorting`.
 *
 * Everything else — multi-sort precedence, the stable `row.index` tiebreak,
 * sub-row recursion and its clone-on-change, `flatRows`, `rowsById`, and the
 * memo key — matches the library's implementation, because the row model is
 * the whole ordering contract and a subtle divergence here reorders the table.
 */
export function createSortedRowModel<TFeatures extends TableFeatures, TData extends RowData>(): (
  table: Table<TFeatures, TData>
) => () => RowModel<TFeatures, TData> {
  return (table) => {
    const sorting = table as unknown as SortingTable<TFeatures, TData>;

    return tableMemo({
      feature: 'rowSortingFeature',
      table,
      fnName: 'table.getSortedRowModel',
      // Identical to the library's: a sort re-runs when the sorting state or
      // the upstream row model changes, and at no other time.
      memoDeps: () => [sorting.atoms.sorting?.get(), sorting.getPreSortedRowModel()],
      fn: () => sortRowModel(sorting),
    });
  };
}

/** One active sort level, resolved once per sort rather than per comparison. */
type SortLevel<TFeatures extends TableFeatures, TData extends RowData> = {
  id: string;
  desc: boolean;
  sortUndefined: SortUndefined | undefined;
  invertSorting: boolean | undefined;
  sortFn: SortFn<TFeatures, TData>;
  sortKey: ((value: TData) => SortKey) | undefined;
};

function sortRowModel<TFeatures extends TableFeatures, TData extends RowData>(
  table: SortingTable<TFeatures, TData>
): RowModel<TFeatures, TData> {
  const preSorted = table.getPreSortedRowModel();
  const sorting = table.atoms.sorting?.get();

  if (!preSorted.rows.length || !sorting?.length) {
    return preSorted;
  }

  const levels: SortLevel<TFeatures, TData>[] = [];
  for (const entry of sorting) {
    const column = table.getColumn(entry.id);
    if (!column?.getCanSort()) {
      continue;
    }

    levels.push({
      id: entry.id,
      desc: entry.desc,
      sortUndefined: column.columnDef.sortUndefined,
      invertSorting: column.columnDef.invertSorting,
      sortFn: column.getSortFn(),
      sortKey: column.columnDef.meta?.sortKey,
    });
  }

  if (!levels.length) {
    return preSorted;
  }

  const flatRows: Row<TFeatures, TData>[] = [];

  return {
    rows: sortRows(preSorted.rows, levels, flatRows).rows,
    flatRows,
    rowsById: preSorted.rowsById,
  };
}

function sortRows<TFeatures extends TableFeatures, TData extends RowData>(
  rows: Row<TFeatures, TData>[],
  levels: SortLevel<TFeatures, TData>[],
  flatRows: Row<TFeatures, TData>[]
): { rows: Row<TFeatures, TData>[]; changed: boolean } {
  // Decorate. One pass per level that publishes a key: n calls, against the
  // ~2·n·log2(n) a comparator would make. Levels after the first are usually
  // never consulted, but computing them lazily would mean a branch inside the
  // comparator — the one place worth keeping empty.
  const keys = levels.map(({ sortKey }) =>
    sortKey ? rows.map((row) => sortKey(row.original)) : undefined
  );

  // Sort a permutation rather than the rows themselves, so a row's key stays
  // reachable by its original position. Pairing keys with rows into objects
  // would allocate n of them and need an undecorate pass.
  const order = rows.map((_, index) => index);

  order.sort((indexA, indexB) => {
    for (let level = 0; level < levels.length; level++) {
      const { id, desc, invertSorting, sortUndefined, sortFn } = levels[level];
      const levelKeys = keys[level];
      let sortInt = 0;

      if (levelKeys) {
        // `compare` rather than localeCompare: a key is now whatever orders the
        // column — a number, a date, or nothing at all for a row the column has
        // no value for — and stringifying those to compare them would order 10
        // before 9. Strings still go through localeCompare inside `compare`, so
        // the columns that keyed on the rendered string keep their order.
        //
        // `sortUndefined` is deliberately not consulted on this path. It governs
        // the library's probe of `row.getValue()`, which defineColumn turns off
        // (it answers a string for every row); nullish *keys* are placed by
        // `compare`, which sends them last ascending and — through the sign flip
        // below — first descending, exactly as the comparators being replaced
        // here did.
        sortInt = compare(levelKeys[indexA], levelKeys[indexB]);
      } else {
        const rowA = rows[indexA];
        const rowB = rows[indexB];

        if (sortUndefined) {
          const aUndefined = rowA.getValue(id) === undefined;
          const bUndefined = rowB.getValue(id) === undefined;

          if (aUndefined && bUndefined) {
            continue;
          }

          if (aUndefined || bUndefined) {
            // 'first'/'last' place undefined absolutely, ahead of the
            // direction flip below — matching the library.
            if (sortUndefined === 'first') {
              return aUndefined ? -1 : 1;
            }

            if (sortUndefined === 'last') {
              return aUndefined ? 1 : -1;
            }

            sortInt = aUndefined ? sortUndefined : -sortUndefined;
          }
        }

        if (sortInt === 0) {
          sortInt = sortFn(rowA, rowB, id);
        }
      }

      if (sortInt !== 0) {
        if (desc) {
          sortInt *= -1;
        }

        if (invertSorting) {
          sortInt *= -1;
        }

        return sortInt;
      }
    }

    // Ties keep their incoming order, which is what makes the sort stable
    // across re-sorts of the same data.
    return rows[indexA].index - rows[indexB].index;
  });

  const sortedData = order.map((index) => rows[index]);

  let changed = false;
  for (let i = 0; i < sortedData.length; i++) {
    const row = sortedData[i];
    if (row !== rows[i]) {
      changed = true;
    }

    const flatIndex = flatRows.length;
    flatRows.push(row);

    if (row.subRows.length) {
      const sortedSubRows = sortRows(row.subRows, levels, flatRows);
      if (sortedSubRows.changed) {
        const cloned = Object.create(Object.getPrototypeOf(row));
        copyInstancePropertiesWithoutMemos(cloned, row);
        cloned.subRows = sortedSubRows.rows;
        sortedData[i] = cloned;
        flatRows[flatIndex] = cloned;
        changed = true;
      }
    }
  }

  return { rows: sortedData, changed };
}
